import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import itLocale from '../locales/it.json';
import enLocale from '../locales/en.json';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Papa from 'papaparse';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ExcelTemplateButton from './ExcelTemplateButton';
import HelpTip from './HelpTip';
import { SURVEY_MAPPING, COHORT_DATE_COUNT } from '../Constants';

dayjs.extend(customParseFormat);

// ---- Survey row type ----
export type SurveyTemplateRow = {
  teamName: string;
  reportType: string;
  leaderMail: string;
  reportDate: string;
  reminderDate: string;
  originalSurveyId: string;
};

// ---- Team row type (new: includes optional cohort fields) ----
export type TeamTemplateRow = {
  teamName: string;
  leaderEmail: string;
  cohortName: string; // '' if no cohort
  systemLeaderEmail: string; // cohort-level, '' if no cohort
  dates: string[]; // up to 5 cohort-level report dates
};

// ---- Cohort bulk input (grouped output for teams mode) ----
export type CohortBulkInput = {
  cohortName: string;
  systemLeaderEmail: string;
  dates: string[];
  teams: { teamName: string; leaderEmail: string }[];
};

// ---- Grouped output for teams mode ----
export type TeamsUploadOutput = {
  cohorts: CohortBulkInput[];
  teamsWithoutCohort: { teamName: string; leaderEmail: string }[];
};

// ---- Props (discriminated union) ----
type ExcelUploadAndParseProps =
  | {
      mode: 'surveys';
      loading: boolean;
      onSubmit: (data: SurveyTemplateRow[]) => void | Promise<void>;
    }
  | {
      mode: 'teams';
      loading: boolean;
      onSubmit: (data: TeamsUploadOutput) => void | Promise<void>;
    };

// ---- Survey-specific constants ----
const SURVEY_EXPECTED_HEADERS = [
  'Team name',
  'Report type',
  'Leader mail address',
  'Reminder date',
  'Report generation date',
] as const;

const ALLOWED_REPORT_TYPES = Object.keys(SURVEY_MAPPING);
type AllowedReportType = (typeof ALLOWED_REPORT_TYPES)[number];

// ---- Team-specific constants ----
const TEAM_DATE_HEADERS = Array.from(
  { length: COHORT_DATE_COUNT },
  (_, i) => `Date ${i + 1}` as const
);
const TEAM_EXPECTED_HEADERS = [
  'Team name',
  'Leader email',
  'Cohort name',
  'System leader email',
  ...TEAM_DATE_HEADERS,
] as const;

// ---- Shared utilities ----
function normalizeHeader(s: unknown): string {
  return String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * Maps normalized alternative-language date header aliases to their canonical
 * English form (e.g. "data 1" → "date 1", "introduzione" → "date 1").
 * Covers numeric patterns and locale default names from all supported locales.
 * Add new locales to the array to extend support automatically.
 */
const HEADER_ALIAS_MAP: Map<string, string> = (() => {
  const map = new Map<string, string>();
  const allLocales = [itLocale, enLocale];

  for (let i = 1; i <= COHORT_DATE_COUNT; i++) {
    const canonical = normalizeHeader(`Date ${i}`);
    // Numeric pattern: "Data N" (Italian)
    map.set(normalizeHeader(`Data ${i}`), canonical);
    // Locale default names (e.g. "Introduzione", "Introduction")
    for (const locale of allLocales) {
      const name: string | undefined = (locale as any).cohorts?.[`date_default_name_${i}`];
      if (name) map.set(normalizeHeader(name), canonical);
    }
  }
  return map;
})();

function resolveHeaderAlias(normalized: string): string {
  return HEADER_ALIAS_MAP.get(normalized) ?? normalized;
}

function isValidEmail(email: string): boolean {
  const e = email.trim();
  if (!e) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(e);
}

/** Validates a field that may contain comma-separated email addresses. */
function isValidEmails(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return trimmed.split(',').every((e) => isValidEmail(e.trim()));
}

function isRowEmpty(values: unknown[]): boolean {
  const cells = values.slice(1).map((v) => String(v ?? '').trim());
  return cells.every((c) => c === '');
}

function cellToString(value: ExcelJS.CellValue | null | undefined): string {
  if (value == null) return '';
  if (value instanceof Date) return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
  const t = typeof value;
  if (t === 'string') return value.toString().trim();
  if (t === 'boolean') return value ? 'true' : 'false';
  if (t === 'number') return Number.isFinite(value) ? String(value) : '';
  if (t === 'object') {
    const v: any = value;
    if (typeof v.text === 'string' && v.text.trim()) return v.text.trim();
    if (Array.isArray(v.richText)) {
      const joined = v.richText.map((p: any) => String(p?.text ?? '')).join('');
      if (joined.trim()) return joined.trim();
    }
    if (v.result != null) return cellToString(v.result);
    if (v.value != null) return cellToString(v.value);
    return String(value).trim();
  }
  return String(value).trim();
}

function cellToEmailString(value: ExcelJS.CellValue | null | undefined): string {
  if (value == null) return '';
  const base = cellToString(value);
  if (base && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(base)) return base.trim();
  if (typeof value === 'object' && !(value instanceof Date)) {
    const v: any = value;
    const hyperlink = typeof v.hyperlink === 'string' ? v.hyperlink : '';
    if (hyperlink) {
      const m = hyperlink.match(/^mailto:([^?]+)/i);
      if (m?.[1]) return decodeURIComponent(m[1]).trim();
      const emailMatch = hyperlink.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
      if (emailMatch?.[0]) return emailMatch[0].trim();
    }
  }
  return base.trim();
}

function parseAndFormatDate(input: unknown): string | null {
  if (input == null) return '';
  if (input instanceof Date) {
    const d = dayjs(input);
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  }
  const raw = String(input ?? '').trim();
  if (!raw) return '';
  const formats = ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'];
  const d = dayjs(raw, formats, true);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

// ---- Survey-specific helpers ----
function normalizeReportType(s: string): string {
  return s.trim().replace(/\s+/g, ' ');
}

function isAllowedReportType(s: string): s is AllowedReportType {
  const normalized = normalizeReportType(s);
  return ALLOWED_REPORT_TYPES.some((t) => t.toLowerCase() === normalized.toLowerCase());
}

function resolveSurveyMapping(reportTypeRaw: string): string {
  const normalized = normalizeReportType(reportTypeRaw);
  if (!normalized) return '';
  if (SURVEY_MAPPING[normalized]) return SURVEY_MAPPING[normalized];
  const key = Object.keys(SURVEY_MAPPING).find((k) => k.toLowerCase() === normalized.toLowerCase());
  return key ? SURVEY_MAPPING[key] : '';
}

// ---- Row validators ----
type TFn = (key: string, opts?: Record<string, string | undefined>) => string;

function validateAndBuildSurveyRow(
  t: TFn,
  args: {
    rowNumber: number;
    teamName: string;
    reportTypeRaw: string;
    leaderMail: string;
    reportDateInput: unknown;
    reminderDateInput: unknown;
  }
): { ok: true; row: SurveyTemplateRow } | { ok: false; error: string } {
  const { rowNumber, teamName, reportTypeRaw, leaderMail, reportDateInput, reminderDateInput } =
    args;
  const issues: string[] = [];

  if (!teamName.trim()) issues.push(t('excel.error_team_name_required'));
  if (!isAllowedReportType(reportTypeRaw))
    issues.push(t('excel.error_report_type', { types: ALLOWED_REPORT_TYPES.join(', ') }));
  if (!isValidEmails(leaderMail)) issues.push(t('excel.error_leader_email'));

  const reportDateParsed = parseAndFormatDate(reportDateInput);
  if (reportDateParsed === null) issues.push(t('excel.error_report_date'));

  const reminderDateParsed = parseAndFormatDate(reminderDateInput);
  if (reminderDateParsed === null) issues.push(t('excel.error_reminder_date'));

  const mappedSurveyId = resolveSurveyMapping(reportTypeRaw);
  if (!mappedSurveyId) issues.push(t('excel.error_no_mapping', { type: reportTypeRaw }));

  if (issues.length > 0) {
    return {
      ok: false,
      error: t('excel.error_row', { row: String(rowNumber), issues: issues.join(' ') }),
    };
  }

  return {
    ok: true,
    row: {
      teamName: teamName.trim(),
      reportType: normalizeReportType(reportTypeRaw),
      leaderMail: leaderMail.trim(),
      reportDate: reportDateParsed ?? '',
      reminderDate: reminderDateParsed ?? '',
      originalSurveyId: mappedSurveyId,
    },
  };
}

function validateAndBuildTeamRow(
  t: TFn,
  args: {
    rowNumber: number;
    teamName: string;
    leaderEmail: string;
    cohortName: string;
    systemLeaderEmail: string;
    dateInputs: unknown[];
  }
): { ok: true; row: TeamTemplateRow } | { ok: false; error: string } {
  const { rowNumber, teamName, leaderEmail, cohortName, systemLeaderEmail, dateInputs } = args;
  const issues: string[] = [];

  if (!teamName.trim()) issues.push(t('excel.error_team_name_required'));
  if (!isValidEmails(leaderEmail)) issues.push(t('excel.error_leader_email'));
  if (cohortName.trim() && !isValidEmail(systemLeaderEmail))
    issues.push(t('excel.error_system_leader_email'));

  const parsedDates: string[] = [];
  for (let i = 0; i < dateInputs.length; i++) {
    const parsed = parseAndFormatDate(dateInputs[i]);
    if (parsed === null) {
      issues.push(t('excel.error_cohort_date', { n: String(i + 1) }));
    } else {
      parsedDates.push(parsed);
    }
  }

  if (issues.length > 0) {
    return {
      ok: false,
      error: t('excel.error_row', { row: String(rowNumber), issues: issues.join(' ') }),
    };
  }

  return {
    ok: true,
    row: {
      teamName: teamName.trim(),
      leaderEmail: leaderEmail.trim(),
      cohortName: cohortName.trim(),
      systemLeaderEmail: systemLeaderEmail.trim(),
      dates: parsedDates.filter(Boolean),
    },
  };
}

/** Group parsed TeamTemplateRow[] into TeamsUploadOutput */
function groupTeamRows(rows: TeamTemplateRow[]): TeamsUploadOutput {
  const cohortMap = new Map<string, CohortBulkInput>();
  const teamsWithoutCohort: { teamName: string; leaderEmail: string }[] = [];

  for (const row of rows) {
    if (!row.cohortName) {
      teamsWithoutCohort.push({ teamName: row.teamName, leaderEmail: row.leaderEmail });
    } else {
      if (!cohortMap.has(row.cohortName)) {
        cohortMap.set(row.cohortName, {
          cohortName: row.cohortName,
          systemLeaderEmail: row.systemLeaderEmail,
          dates: row.dates,
          teams: [],
        });
      }
      cohortMap
        .get(row.cohortName)!
        .teams.push({ teamName: row.teamName, leaderEmail: row.leaderEmail });
    }
  }

  return { cohorts: Array.from(cohortMap.values()), teamsWithoutCohort };
}

export default function ExcelUploadAndParse(props: ExcelUploadAndParseProps) {
  const { mode, loading, onSubmit } = props;
  const { t } = useTranslation();
  const [rows, setRows] = useState<SurveyTemplateRow[] | TeamTemplateRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const expectedHeaders = mode === 'surveys' ? SURVEY_EXPECTED_HEADERS : TEAM_EXPECTED_HEADERS;

  const expectedHeaderSet = useMemo(
    () => new Set(expectedHeaders.map((h) => normalizeHeader(h))),
    [expectedHeaders]
  );

  const parseXlsx = async (file: File) => {
    const buf = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buf);

    const worksheet = workbook.worksheets[0];
    if (!worksheet) throw new Error(t('excel.error_no_worksheet'));

    const headerRow = worksheet.getRow(1);
    const headerValues = (headerRow.values ?? []) as unknown[];

    const colToHeader = new Map<number, string>();
    for (let col = 1; col < headerValues.length; col++) {
      const h = resolveHeaderAlias(normalizeHeader(headerValues[col]));
      if (h) colToHeader.set(col, h);
    }

    const presentHeaders = new Set(Array.from(colToHeader.values()));
    for (const h of Array.from(expectedHeaderSet)) {
      if (!presentHeaders.has(h)) throw new Error(t('excel.error_missing_header', { header: h }));
    }

    const headerToCol = new Map<string, number>();
    Array.from(colToHeader.entries()).forEach(([col, h]) => headerToCol.set(h, col));

    const parsed: (SurveyTemplateRow | TeamTemplateRow)[] = [];
    const rowErrors: string[] = [];

    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return;
      const values = (row.values ?? []) as unknown[];
      if (isRowEmpty(values)) return;

      const getCellValue = (header: string) => {
        const col = headerToCol.get(normalizeHeader(header));
        if (!col) return null;
        return row.getCell(col).value ?? null;
      };

      let res:
        | { ok: true; row: SurveyTemplateRow | TeamTemplateRow }
        | { ok: false; error: string };

      if (mode === 'surveys') {
        res = validateAndBuildSurveyRow(t, {
          rowNumber,
          teamName: cellToString(getCellValue('Team name')),
          reportTypeRaw: cellToString(getCellValue('Report type')),
          leaderMail: cellToEmailString(getCellValue('Leader mail address')),
          reportDateInput: getCellValue('Report generation date'),
          reminderDateInput: getCellValue('Reminder date'),
        });
      } else {
        res = validateAndBuildTeamRow(t, {
          rowNumber,
          teamName: cellToString(getCellValue('Team name')),
          leaderEmail: cellToEmailString(getCellValue('Leader email')),
          cohortName: cellToString(getCellValue('Cohort name')),
          systemLeaderEmail: cellToEmailString(getCellValue('System leader email')),
          dateInputs: TEAM_DATE_HEADERS.map((h) => getCellValue(h)),
        });
      }

      if (!res.ok) {
        rowErrors.push(res.error);
        return;
      }
      parsed.push(res.row);
    });

    if (rowErrors.length > 0) throw new Error(rowErrors.join('\n'));
    setRows(parsed as any);
  };

  const parseCsvFile = async (file: File) => {
    const text = await file.text();
    const result = Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: 'greedy',
      delimiter: '',
      transformHeader: (h) => resolveHeaderAlias(normalizeHeader(h)),
      transform: (v) => String(v ?? '').trim(),
    });

    if (result.errors?.length) {
      const errs = result.errors
        .slice(0, 5)
        .map((e) => e.message)
        .join('\n');
      throw new Error(t('excel.error_csv_parse', { errors: errs }));
    }

    const data = result.data ?? [];
    if (data.length === 0) throw new Error(t('excel.error_csv_empty'));

    const headersPresent = new Set(Object.keys(data[0] ?? {}));
    for (const h of Array.from(expectedHeaderSet)) {
      if (!headersPresent.has(h)) throw new Error(t('excel.error_missing_header', { header: h }));
    }

    const parsed: (SurveyTemplateRow | TeamTemplateRow)[] = [];
    const rowErrors: string[] = [];

    data.forEach((record, idx) => {
      const rowNumber = idx + 2;
      let res:
        | { ok: true; row: SurveyTemplateRow | TeamTemplateRow }
        | { ok: false; error: string };

      if (mode === 'surveys') {
        res = validateAndBuildSurveyRow(t, {
          rowNumber,
          teamName: String(record[normalizeHeader('Team name')] ?? '').trim(),
          reportTypeRaw: String(record[normalizeHeader('Report type')] ?? '').trim(),
          leaderMail: String(record[normalizeHeader('Leader mail address')] ?? '').trim(),
          reportDateInput: record[normalizeHeader('Report generation date')] ?? '',
          reminderDateInput: record[normalizeHeader('Reminder date')] ?? '',
        });
      } else {
        res = validateAndBuildTeamRow(t, {
          rowNumber,
          teamName: String(record[normalizeHeader('Team name')] ?? '').trim(),
          leaderEmail: String(record[normalizeHeader('Leader email')] ?? '').trim(),
          cohortName: String(record[normalizeHeader('Cohort name')] ?? '').trim(),
          systemLeaderEmail: String(record[normalizeHeader('System leader email')] ?? '').trim(),
          dateInputs: TEAM_DATE_HEADERS.map((h) => record[normalizeHeader(h)] ?? ''),
        });
      }

      if (!res.ok) {
        rowErrors.push(res.error);
        return;
      }
      parsed.push(res.row);
    });

    if (rowErrors.length > 0) throw new Error(rowErrors.join('\n'));
    setRows(parsed as any);
  };

  const parseFile = async (file: File) => {
    setError(null);
    setRows([]);
    setFileName(file.name);

    const lower = file.name.toLowerCase();
    const isXlsx =
      lower.endsWith('.xlsx') ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const isCsv =
      lower.endsWith('.csv') ||
      file.type === 'text/csv' ||
      file.type === 'application/csv' ||
      file.type === 'text/plain';

    if (!isXlsx && !isCsv) throw new Error(t('excel.error_wrong_format'));
    if (isXlsx) return parseXlsx(file);
    return parseCsvFile(file);
  };

  const onChooseFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await parseFile(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('excel.error_parse_failed'));
      setRows([]);
      setFileName(file.name);
    } finally {
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (mode === 'surveys') {
      await (onSubmit as (data: SurveyTemplateRow[]) => void | Promise<void>)(
        rows as SurveyTemplateRow[]
      );
    } else {
      await (onSubmit as (data: TeamsUploadOutput) => void | Promise<void>)(
        groupTeamRows(rows as TeamTemplateRow[])
      );
    }
    setRows([]);
    setError(null);
    setFileName(null);
  };

  const titleKey = mode === 'surveys' ? 'excel.title' : 'excel.teams_title';
  const readyKey = mode === 'surveys' ? 'excel.ready' : 'excel.teams_ready';

  const previewHeaders = useMemo(() => {
    if (mode === 'surveys') {
      return [
        t('teams.name'),
        t('excel.col_report_type'),
        t('surveys.leader_email'),
        t('surveys.reminder_date'),
        t('surveys.report_date'),
      ];
    }
    return [
      t('teams.name'),
      t('teams.leader'),
      t('cohorts.name'),
      t('cohorts.system_leader'),
      ...Array.from({ length: COHORT_DATE_COUNT }, (_, i) =>
        t(`cohorts.date_default_name_${i + 1}`)
      ),
    ];
  }, [mode, t]);

  const renderPreviewRow = (row: SurveyTemplateRow | TeamTemplateRow): string[] => {
    if (mode === 'surveys') {
      const r = row as SurveyTemplateRow;
      return [r.teamName, r.reportType, r.leaderMail, r.reminderDate, r.reportDate];
    } else {
      const r = row as TeamTemplateRow;
      return [
        r.teamName,
        r.leaderEmail,
        r.cohortName,
        r.systemLeaderEmail,
        ...r.dates,
        ...Array(COHORT_DATE_COUNT - r.dates.length).fill(''),
      ];
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box padding={4}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column-reverse', sm: 'column-reverse', md: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent={'space-between'}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body1" textAlign={'left'}>
                    {t(titleKey)}
                  </Typography>
                  {mode === 'teams' && <HelpTip text={t('help.tips.excel_date_headers')} />}
                </Stack>
                <Typography variant="body2" textAlign={'left'} color="text.secondary">
                  {t('excel.subtitle')}
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => inputRef.current?.click()}>
                {t('excel.choose_file')}
              </Button>
              {fileName && (
                <Typography variant="body2" color="text.secondary">
                  {fileName}
                </Typography>
              )}
            </Stack>
            <Box>
              <ExcelTemplateButton mode={mode} />
            </Box>
          </Stack>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={onChooseFile}
          />

          {error && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {error}
            </Alert>
          )}

          {rows.length > 0 && (
            <>
              <Alert severity="success">{t(readyKey, { count: rows.length })}</Alert>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {previewHeaders.map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.slice(0, 5).map((r, idx) => (
                      <TableRow key={idx}>
                        {renderPreviewRow(r).map((cell, ci) => (
                          <TableCell key={ci}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    setRows([]);
                    setError(null);
                    setFileName(null);
                  }}
                >
                  {t('excel.clear')}
                </Button>
                <Button variant="contained" disabled={loading} onClick={handleSubmit}>
                  {t('excel.upload')}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
